import React from "react";
import { Typography, Stack } from "@altimateai/lego";
import { ProgressBar } from "./ProgressBar";
import { TaskProgressResponse } from "../../services/adeBench";

interface ProgressStatsProps {
  progressData: TaskProgressResponse | undefined;
  progressLoading: boolean;
  selectedRunIds: Set<string>;
  failingCount: number;
  partialCount: number;
  solvedCount: number;
}

export const ProgressStats: React.FC<ProgressStatsProps> = ({
  progressData,
  progressLoading,
  selectedRunIds,
  failingCount,
  partialCount,
  solvedCount,
}) => {
  const hasSelection = selectedRunIds.size > 0;

  return (
    <div className="al-mb-6">
      <div
        className={`al-transition-opacity ${progressLoading && hasSelection && progressData ? "al-opacity-50" : ""}`}
      >
        <div className="al-grid al-grid-cols-2 al-gap-4 al-mb-3">
          <div className="al-bg-white al-rounded-lg al-border al-p-4 al-min-h-[100px]">
            <Typography className="al-text-gray-500 al-text-sm al-mb-1">Tasks Solved</Typography>
            {progressLoading && hasSelection ? (
              <div className="al-animate-pulse al-h-8 al-bg-gray-200 al-rounded al-w-full al-mt-2" />
            ) : progressData && hasSelection ? (
              <>
                <Stack direction="row" align="baseline" gap="sm">
                  <Typography className="al-text-xl al-font-bold">
                    {progressData.tasks_solved}/{progressData.tasks_total}
                  </Typography>
                  <Typography className="al-text-lg al-text-gray-500">
                    ({progressData.tasks_solved_pct.toFixed(1)}%)
                  </Typography>
                </Stack>
                <div className="al-mt-2">
                  <ProgressBar value={progressData.tasks_solved} max={progressData.tasks_total} />
                </div>
              </>
            ) : (
              <>
                <Stack direction="row" align="baseline" gap="sm">
                  <Typography className="al-text-xl al-font-bold">0/0</Typography>
                  <Typography className="al-text-lg al-text-gray-500">(0.0%)</Typography>
                </Stack>
                <div className="al-mt-2">
                  <ProgressBar value={0} max={1} />
                </div>
              </>
            )}
          </div>
          <div className="al-bg-white al-rounded-lg al-border al-p-4 al-min-h-[100px]">
            <Typography className="al-text-gray-500 al-text-sm al-mb-1">Tests Passing</Typography>
            {progressLoading && hasSelection ? (
              <div className="al-animate-pulse al-h-8 al-bg-gray-200 al-rounded al-w-full al-mt-2" />
            ) : progressData && hasSelection ? (
              <>
                <Stack direction="row" align="baseline" gap="sm">
                  <Typography className="al-text-xl al-font-bold">
                    {progressData.tests_passing}/{progressData.tests_total}
                  </Typography>
                  <Typography className="al-text-lg al-text-gray-500">
                    ({progressData.tests_passing_pct.toFixed(1)}%)
                  </Typography>
                </Stack>
                <div className="al-mt-2">
                  <ProgressBar
                    value={progressData.tests_passing}
                    max={progressData.tests_total}
                    colorClass="al-bg-blue-500"
                  />
                </div>
              </>
            ) : (
              <>
                <Stack direction="row" align="baseline" gap="sm">
                  <Typography className="al-text-xl al-font-bold">0/0</Typography>
                  <Typography className="al-text-lg al-text-gray-500">(0.0%)</Typography>
                </Stack>
                <div className="al-mt-2">
                  <ProgressBar value={0} max={1} colorClass="al-bg-blue-500" />
                </div>
              </>
            )}
          </div>
        </div>
        <Stack direction="row" align="center" gap="md">
          <Typography className="al-text-sm al-text-red-600">✗ {failingCount} failing</Typography>
          <Typography className="al-text-sm al-text-yellow-600">
            ◐ {partialCount} partial
          </Typography>
          <Typography className="al-text-sm al-text-green-600">✓ {solvedCount} solved</Typography>
          <Typography className="al-text-sm al-text-gray-500 al-ml-auto">
            {selectedRunIds.size} run{selectedRunIds.size !== 1 ? "s" : ""} selected
          </Typography>
        </Stack>
      </div>
    </div>
  );
};
